
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  LayoutDashboard,
  Menu,
  HeartHandshake,
  UserRound,
  FileBarChart2,
  MessageSquare,
  MapPin,
  ChevronDown,
  ChevronRight,
  PlusCircle,
  ListChecks,
  BarChart3,
  SendHorizonal,
  TrendingUp,
  Users,
  Mail,
  MailOpen,
  Settings,
  ArrowLeftRight,
  Zap
} from "lucide-react";
import { cn } from "@/lib/utils";

const AdminSidebar = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = React.useState(false);
  const [expandedMenus, setExpandedMenus] = React.useState<Record<string, boolean>>({
    partners: location.pathname.includes("/admin/partners"),
    messages: location.pathname.includes("/admin/messages"),
    leads: location.pathname.includes("/admin/leads"),
    reports: location.pathname.includes("/admin/reports"),
    emails: location.pathname.includes("/admin/email"),
  });

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const closeSidebar = () => {
    setIsOpen(false);
  };

  const toggleSubmenu = (key: string) => {
    setExpandedMenus(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (path: string, exact = false) => {
    return exact ? location.pathname === path : location.pathname.startsWith(path);
  };

  const menuItems = [
    {
      title: "Dashboard",
      icon: <LayoutDashboard className="h-5 w-5" />,
      path: "/admin",
      exact: true,
    },
    {
      title: "Συνεργάτες",
      icon: <HeartHandshake className="h-5 w-5" />,
      path: "/admin/partners",
      key: "partners",
      submenu: [
        {
          title: "Λίστα Συνεργατών",
          path: "/admin/partners",
          exact: true,
          icon: <ListChecks className="h-4 w-4" />,
        },
        {
          title: "Χάρτης Συνεργατών",
          path: "/admin/partners/map",
          icon: <MapPin className="h-4 w-4" />,
        },
        {
          title: "Νέος Συνεργάτης",
          path: "/admin/partners/new",
          icon: <PlusCircle className="h-4 w-4" />,
        },
      ],
    },
    {
      title: "Leads",
      icon: <UserRound className="h-5 w-5" />,
      path: "/admin/leads",
      key: "leads",
      submenu: [
        {
          title: "Όλα τα Leads",
          path: "/admin/leads",
          exact: true,
          icon: <ListChecks className="h-4 w-4" />,
        },
        {
          title: "Ανάθεση Leads",
          path: "/admin/leads/assignment",
          icon: <ArrowLeftRight className="h-4 w-4" />,
        },
        {
          title: "Στατιστικά Leads",
          path: "/admin/leads/statistics",
          icon: <BarChart3 className="h-4 w-4" />,
        },
      ],
    },
    {
      title: "Αναφορές",
      icon: <FileBarChart2 className="h-5 w-5" />,
      path: "/admin/reports",
      key: "reports",
      submenu: [
        {
          title: "Γενικές Αναφορές",
          path: "/admin/reports",
          exact: true,
          icon: <FileBarChart2 className="h-4 w-4" />,
        },
        {
          title: "Αναφορές Συνεργατών",
          path: "/admin/reports/partners",
          icon: <TrendingUp className="h-4 w-4" />,
        },
      ],
    },
    {
      title: "Μηνύματα",
      icon: <MessageSquare className="h-5 w-5" />,
      path: "/admin/messages",
      key: "messages",
      submenu: [
        {
          title: "Αποστολή SMS",
          path: "/admin/messages",
          exact: true,
          icon: <SendHorizonal className="h-4 w-4" />,
        },
        {
          title: "Στατιστικά SMS",
          path: "/admin/messages/statistics",
          icon: <BarChart3 className="h-4 w-4" />,
        },
      ],
    },
    {
      title: "Emails",
      icon: <Mail className="h-5 w-5" />,
      path: "/admin/email",
      key: "emails",
      submenu: [
        {
          title: "Στατιστικά Emails",
          path: "/admin/email/statistics",
          icon: <MailOpen className="h-4 w-4" />,
        },
      ],
    },
    {
      title: "Διαχειριστές",
      icon: <Users className="h-5 w-5" />,
      path: "/admin/admins",
      exact: true,
    },
    {
      title: "Ρυθμίσεις",
      icon: <Settings className="h-5 w-5" />,
      path: "/admin/settings",
      exact: true,
    },
  ];

  return (
    <div className={cn(
      "flex flex-col min-h-screen bg-sidebar text-sidebar-foreground",
      isMobile ? "fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 ease-in-out" : "w-72",
      isMobile && !isOpen ? "-translate-x-full" : "translate-x-0"
    )}>
      {/* Logo Section */}
      <div className="flex items-center gap-3 p-6 border-b border-sidebar-border">
        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg">
          <Zap className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="font-bold text-lg text-sidebar-foreground">Lead Manager</h1>
          <p className="text-xs text-slate-400">Διαθερμική</p>
        </div>
        {isMobile && (
          <Button variant="ghost" size="icon" onClick={toggleSidebar} className="ml-auto text-sidebar-foreground">
            <Menu className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-auto py-4 px-3">
        <ul className="space-y-1">
          {menuItems.map((item) => (
            <li key={item.title}>
              {item.submenu ? (
                <>
                  <button
                    onClick={() => toggleSubmenu(item.key as string)}
                    className={cn(
                      "nav-item w-full justify-between",
                      isActive(item.path) && !expandedMenus[item.key as string]
                        ? "nav-item-active"
                        : "nav-item-inactive"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      {item.icon}
                      <span>{item.title}</span>
                    </div>
                    {expandedMenus[item.key as string] ? (
                      <ChevronDown className="h-4 w-4 transition-transform" />
                    ) : (
                      <ChevronRight className="h-4 w-4 transition-transform" />
                    )}
                  </button>

                  {expandedMenus[item.key as string] && (
                    <ul className="mt-1 ml-4 pl-4 space-y-1 border-l border-slate-700 animate-slide-in-left">
                      {item.submenu.map((subItem) => (
                        <li key={subItem.title}>
                          <Link
                            to={subItem.path}
                            onClick={closeSidebar}
                            className={cn(
                              "nav-item text-sm",
                              isActive(subItem.path, subItem.exact)
                                ? "text-blue-400 bg-blue-500/10"
                                : "text-slate-400 hover:text-white hover:bg-white/5"
                            )}
                          >
                            {subItem.icon}
                            <span>{subItem.title}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <Link
                  to={item.path}
                  onClick={closeSidebar}
                  className={cn(
                    "nav-item",
                    isActive(item.path, item.exact)
                      ? "nav-item-active"
                      : "nav-item-inactive"
                  )}
                >
                  {item.icon}
                  <span>{item.title}</span>
                </Link>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <span className="text-xs font-bold text-white">Δ</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">Διαθερμική</p>
            <p className="text-xs text-slate-400">Admin Panel</p>
          </div>
        </div>
        <p className="mt-4 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} Διαθερμική
        </p>
      </div>

      {/* Mobile backdrop */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[-1]"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default AdminSidebar;
